import Button from "../../../../../components/shared/Button/Button";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { twMerge } from "tailwind-merge";
import { fuzzySearch } from "../../../utils/fuzzySearch";
import { useId } from "../../../../../hooks/useId";
import Divider from "../../../../../components/shared/Divider/Divider";
import Checkbox from "../../../../../components/shared/Checkbox";

export function SelectTags({
  header,
  onChange,
  onClear,
  options,
  placeholder = "Tags",
  values,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const summary = useMemo(() => {
    if (!values?.length) return placeholder;
    const first = options.find((o) => String(o) === String(values[0]));
    const extra = values.length > 1 ? `+${values.length - 1}` : "";
    return `${first ?? values[0]} ${extra}`.trim();
  }, [options, values, placeholder]);

  const filtered = useMemo(
    () => fuzzySearch(search, options),
    [search, options]
  );

  const close = () => setOpen(false);

  // outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // keep selected visible when opening
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current?.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const renderOption = (el, isChecked) => (
    <button
      aria-selected={isChecked}
      className={twMerge(
        "flex w-full cursor-pointer items-center gap-2 rounded-sm p-2.5 text-left text-sm",
        "hover:bg-gray100 focus:bg-gray100 focus:outline-none"
      )}
      data-active={isChecked ? "true" : undefined}
      key={String(el)}
      onClick={() => onChange(el)}
      role="option"
      type="button"
    >
      <Checkbox isChecked={isChecked} />

      <span className="truncate">{String(el)}</span>
    </button>
  );

  return (
    <div className={twMerge("relative")} ref={rootRef}>
      <Button
        className="flex flex-row items-center gap-2"
        color={values?.length ? "primary50" : "gray100"}
        onClick={() => setOpen((o) => !o)}
        ref={buttonRef}
        rounded="full"
        size="sm"
      >
        {summary}
        <FiChevronDown
          className={twMerge(
            "shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="absolute z-10 mt-1 w-[200px]"
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            <div className="border-gray200 max-h-96 overflow-hidden rounded-md border bg-white shadow-lg">
              {header && (
                <>
                  <div className="px-4 py-3 text-base font-semibold text-black">
                    {header}
                  </div>
                  <Divider color="gray200" type="horizontal" />
                </>
              )}

              {/* Search */}
              <div className="p-3">
                <input
                  autoFocus
                  className={twMerge(
                    "border-gray300 h-9 w-full rounded-md border px-4 text-sm outline-none",
                    "focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
                  )}
                  onChange={(e) =>
                    setSearch(e.target.value.replace(/^\s+/, ""))
                  }
                  placeholder="Search"
                  value={search}
                />
              </div>

              <Divider color="gray200" type="horizontal" />

              {/* Options */}
              <div
                className="max-h-72 overflow-y-auto"
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
              >
                <div className="flex flex-col gap-1 px-1.5 py-1">
                  {filtered.map((el) => {
                    const isActive = values?.some(
                      (v) => String(v) === String(el)
                    );
                    return <>{renderOption(el, isActive)}</>;
                  })}
                  {!filtered.length && (
                    <div className="text-gray500 p-3 text-sm">No matches</div>
                  )}
                </div>
              </div>

              <Divider color="gray200" type="horizontal" />

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-4 py-3">
                <button
                  className={twMerge(
                    "text-sm font-semibold",
                    values?.length
                      ? "text-error600 hover:underline"
                      : "text-gray400 cursor-default"
                  )}
                  onClick={() => {
                    onClear?.();
                    setSearch("");
                  }}
                  type="button"
                >
                  Clear
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
